import { useMemo, useState } from "react";
import {
  useLists,
  useTasks,
  useCreateList,
  useUpdateList,
  useDeleteList,
} from "../hooks/useTasks";
import { useUiStore } from "../state/uiStore";
import type { TmListEntity, TaskVisibility } from "../api/tm";

const VISIBILITIES: { value: TaskVisibility; label: string }[] = [
  { value: "private", label: "Privée" },
  { value: "team", label: "Équipe" },
  { value: "tenant", label: "Tenant" },
];

export function ListsScreen() {
  const lists = useLists();
  const tasks = useTasks();
  const createList = useCreateList();
  const updateList = useUpdateList();
  const deleteList = useDeleteList();
  const prefs = useUiStore((s) => s.prefs);
  const [newName, setNewName] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const [confirmDelete, setConfirmDelete] = useState<TmListEntity | null>(null);

  const countByList = useMemo(() => {
    const m: Record<string, number> = {};
    for (const t of tasks.data ?? []) {
      if (t.list_id) m[t.list_id] = (m[t.list_id] ?? 0) + 1;
    }
    return m;
  }, [tasks.data]);

  const add = () => {
    const name = newName.trim();
    if (!name) return;
    createList.mutate(
      { name, visibility: prefs.defaultVisibility },
      { onSuccess: () => setNewName("") },
    );
  };

  const rename = (id: string) => {
    const name = draft.trim();
    if (name) updateList.mutate({ id, body: { name } });
    setEditingId(null);
  };

  return (
    <>
      <div className="view-header">
        <div className="view-title">Listes</div>
        <div className="view-caption">{lists.data?.length ?? 0} listes</div>
      </div>
      <div className="main-scroll">
        <div style={{ marginTop: 14, maxWidth: 720 }}>
          <div className="quick-add">
            <span style={{ color: "var(--ink-ghost)", fontSize: 15 }}>＋</span>
            <input
              value={newName}
              placeholder="Nouvelle liste…"
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && add()}
              aria-label="Nom de la nouvelle liste"
            />
            {newName.trim() && (
              <button
                className="btn primary"
                onClick={add}
                disabled={createList.isPending}
              >
                Créer
              </button>
            )}
          </div>

          <table className="data-table" style={{ marginTop: 18 }}>
            <thead>
              <tr>
                <th>Liste</th>
                <th>Visibilité</th>
                <th>Tâches</th>
                <th style={{ width: 20 }} />
              </tr>
            </thead>
            <tbody>
              {(lists.data ?? []).map((l) => (
                <tr key={l.id}>
                  <td>
                    {editingId === l.id ? (
                      <input
                        autoFocus
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        onBlur={() => rename(l.id)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") rename(l.id);
                          if (e.key === "Escape") setEditingId(null);
                        }}
                        style={{
                          font: "inherit",
                          border: "1px solid var(--accent-border)",
                          borderRadius: 6,
                          padding: "2px 6px",
                        }}
                        aria-label={`Renommer ${l.name}`}
                      />
                    ) : (
                      <button
                        style={{ fontWeight: 600, cursor: "text" }}
                        title="Cliquer pour renommer"
                        onClick={() => {
                          setEditingId(l.id);
                          setDraft(l.name);
                        }}
                      >
                        {l.icon ? `${l.icon} ` : ""}
                        {l.name}
                      </button>
                    )}
                  </td>
                  <td>
                    <select
                      value={l.visibility}
                      onChange={(e) =>
                        updateList.mutate({
                          id: l.id,
                          body: {
                            visibility: e.target.value as TaskVisibility,
                          },
                        })
                      }
                      style={{
                        font: "inherit",
                        border: "1px solid var(--border-3)",
                        borderRadius: 6,
                        padding: "2px 6px",
                        background: "var(--bg)",
                      }}
                      aria-label={`Visibilité de ${l.name}`}
                    >
                      {VISIBILITIES.map((v) => (
                        <option key={v.value} value={v.value}>
                          {v.label}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>{countByList[l.id] ?? 0}</td>
                  <td>
                    <button
                      className="sidebar-collapse"
                      title="Supprimer la liste"
                      onClick={() => setConfirmDelete(l)}
                    >
                      🗑
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {lists.data?.length === 0 && (
            <div className="empty-state">
              Aucune liste — créez la première ci-dessus.
            </div>
          )}
        </div>
      </div>

      {confirmDelete && (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgb(0 0 0 / 25%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 50,
          }}
          onClick={() => setConfirmDelete(null)}
        >
          <div
            style={{
              background: "var(--bg)",
              borderRadius: 12,
              padding: "20px 22px",
              width: 380,
              boxShadow: "0 8px 30px rgb(0 0 0 / 15%)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ fontWeight: 700, fontSize: 15 }}>
              Supprimer la liste
            </div>
            <p
              style={{
                fontSize: 12.5,
                color: "var(--ink-muted)",
                lineHeight: 1.5,
              }}
            >
              Cette action est <b>irréversible</b>. Les{" "}
              {countByList[confirmDelete.id] ?? 0} tâches de cette liste seront{" "}
              <b>déliées</b> (pas supprimées).
            </p>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 8,
                marginTop: 14,
              }}
            >
              <button className="btn" onClick={() => setConfirmDelete(null)}>
                Annuler
              </button>
              <button
                className="btn danger"
                onClick={() => {
                  deleteList.mutate(confirmDelete.id);
                  setConfirmDelete(null);
                }}
              >
                Supprimer la liste
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
